import { CURRENCY, NO_APLICA, NOT_SET, buildEventItemId } from './constants'
import { slugify } from './dataLayer'
import type { EcommerceItem, EcommerceItemBase } from './types'
import type { ProductApiItem } from '@/app/lib/api/types-api'
import { resolveProductGenre } from '@/app/lib/utils'

/**
 * `ProductApiItem` → ítem de e-commerce SIN `index`
 * (`components-fingerprint/Listado filtrado.csv` → «Renderizado del listado»,
 * ejemplo completo en `guides/02_Snippets&DataLayer.md` §6).
 *
 * Deliberadamente separado de `mapProductToCard` (`src/app/lib/api/`): ese
 * mapper resuelve la PRESENTACIÓN de la card (precio formateado, estado de
 * compra, imagen) y descarta `id`/el género (`resolveProductGenre`), que aquí
 * hacen falta.
 *
 * El `index` va aparte (`mapProductToAnalyticsItem`) porque no es un dato del
 * producto sino de su posición en un listado concreto: el mismo evento aparece
 * en varias listas de la misma página (listado principal, "otras ciudades",
 * vistos recientemente) con índices distintos, y en "vistos recientemente" la
 * posición además cambia al quitar tarjetas del carril.
 */
export function mapProductToAnalyticsItemBase(product: ProductApiItem): EcommerceItemBase {
  const genre = resolveProductGenre(product.genres)
  return {
    item_id: buildEventItemId(product.url),
    item_name: slugify(product.title),
    // `item_brand` NO es el artista: el propio ejemplo de la guía lo deja en
    // `not_set` para conciertos con artista conocido (ese dato va en `artist`).
    item_brand: NOT_SET,
    item_variant: product.venue?.city ? slugify(product.venue.city) : NOT_SET,
    item_category: genre?.nicename || NOT_SET,
    item_category2: genre?.style_nicename || NOT_SET,
    price: product.min_price,
    currency: CURRENCY,
    // `start_at` es un ISO datetime; el diccionario espera solo la fecha.
    start_date: product.start_at?.slice(0, 10) ?? NOT_SET,
    // `ProductApiItem` no expone artista. Pendiente de que backend lo añada.
    artist: NOT_SET,
  }
}

/**
 * `ProductApiItem` → `items[]` de `view_item_list`. `index` entra 0-based
 * (posición en el array acumulado) y sale 1-based, como en los ejemplos de la
 * guía.
 */
export function mapProductToAnalyticsItem(product: ProductApiItem, index: number): EcommerceItem {
  return withIndex(mapProductToAnalyticsItemBase(product), index)
}

/** Fija la posición 1-based de un ítem ya mapeado dentro de su listado. */
export function withIndex(item: EcommerceItemBase, index: number): EcommerceItem {
  return { ...item, index: index + 1 }
}

/**
 * Mismo ítem, adaptado a `select_item`: el CSV fija `item_brand`,
 * `item_category2` y `start_date` a `no_aplica` en ese evento (a diferencia de
 * `view_item_list`, donde sí llevan valor real). ⚠️ La ficha `Grid Cards.csv`
 * los manda con valor en ambos eventos — se sigue `Listado filtrado.csv` por
 * ser la ficha del componente que las PLP de este repo usan realmente.
 */
export function toSelectItem(item: EcommerceItem): EcommerceItem {
  return {
    ...item,
    item_brand: NO_APLICA,
    item_category2: NO_APLICA,
    start_date: NO_APLICA,
  }
}
